import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { customReportPropTypes, customReportsPropTypes } from '../../shared/customReports/propTypes';
import CustomReportsEditorForm from './customReportsEditorForm.component';

import DeleteCustomReportModal from './deleteCustomReportModal.component';

import Button from '../../shared/button.component';
import CustomReportsGrid from '../../shared/customReports/customReportsGrid.component';

import { withPermissions, permissions } from '../../shared/authorization';
import Spinner from '../../shared/spinner.component';

import {
  customReportsSelector,
  openFormForAddSelector,
  openFormForEditSelector,
  customReportIdSelector,
  isFetchingCustomReportsSelector,
  isTestCustomReportQueryInProgressSelector,
  rowToScrollToSelector,
  addedReportIdSelector,
} from './store/selectors';

import {
  showCustomReportFormForEdit,
  showCustomReportFormForAdd,
  fetchCustomReports,
  updateDisplayFlag,
  updateRowToScrollTo,
  resetRowToScrollTo,
  deleteCustomReport,
} from './store/actions';

import ModalNames from '../../shared/customReports/modalNames';

import { modalDataSelector } from '../../shared/modals/modals.selectors';
import { ExportCustomRepostInsertQueryModal } from './exportCustomRepostInsertQueryModal.component';

class CustomReportsManagementPage extends PureComponent {
  static propTypes = {
    customReports: customReportsPropTypes,
    openFormForAdd: PropTypes.bool.isRequired,
    openFormForEdit: PropTypes.bool.isRequired,
    canModify: PropTypes.bool.isRequired,
    isFetchingCustomReports: PropTypes.bool.isRequired,
    isTestCustomReportQueryInProgress: PropTypes.bool.isRequired,
    rowToScrollTo: PropTypes.number,
    addedReportId: PropTypes.number,

    customReportToEditId: PropTypes.string,

    showCustomReportFormForEdit: PropTypes.func.isRequired,
    showCustomReportFormForAdd: PropTypes.func.isRequired,
    fetchCustomReports: PropTypes.func.isRequired,
    updateDisplayFlag: PropTypes.func.isRequired,
    resetRowToScrollTo: PropTypes.func.isRequired,
    updateRowToScrollTo: PropTypes.func.isRequired,

    deleteCustomReport: PropTypes.func.isRequired,
    deleteModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    }),
    customRepostInsertQueryModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      reportSetId: PropTypes.string,
      data: customReportPropTypes,
    }),
  };

  state = {
    isCustomReportQueryModified: false,
  };

  componentDidMount() {
    const { fetchCustomReports } = this.props;
    fetchCustomReports();
  }

  componentDidUpdate(prevProps) {
    const {
      rowToScrollTo,
      addedReportId,
    } = this.props;

    if (prevProps.customReports.length === 0 && this.customReportsGridApi) {
      this.selectFirstRow();
    }
    const isNewRowAdded = prevProps.customReports.length + 1 === this.props.customReports.length;
    if (addedReportId && isNewRowAdded) {
      this.customReportsGridApi.forEachNode((node, index) => {
        if (node.data.reportSetId === addedReportId) {
          this.scrollToRowIndex(index);
          node.setSelected(true, true);
        }
      });
    }
    if (rowToScrollTo !== null && prevProps.customReports.length !== this.props.customReports.length) {
      const nodeOnTop = this.scrollToRowIndex(rowToScrollTo);
      if (nodeOnTop) {
        nodeOnTop.setSelected(true, true);
      }
    }
  }

  selectFirstRow = () => {
    const firstRow = this.customReportsGridApi.getModel().getRow(0);

    if (firstRow) {
      this.showCustomReportQueryForm(firstRow.data.reportSetId);
      firstRow.setSelected(true, true);
    }
  };

  scrollToRowIndex = rowToScrollTo => {
    const node = this.customReportsGridApi.getDisplayedRowAtIndex(rowToScrollTo);
    this.customReportsGridApi.ensureNodeVisible(node, 'top');
    this.customReportsGridApi.deselectAll();
    this.customReportsGridApi.setFocusedCell(-1);
    this.props.resetRowToScrollTo();
    return node;
  };

  onCustomReportGridReady = params => {
    this.customReportsGridApi = params.api;
    params.api.sizeColumnsToFit();
    this.selectFirstRow();
  };

  showCustomReportQueryForm = customReportId => {
    const { customReportToEditId, deleteModalData, showCustomReportFormForEdit } = this.props;

    const alreadyOpeningDeleteModal = deleteModalData.data && customReportId === deleteModalData.data.id;
    const alreadySelectedForViewOrEdit = customReportId === customReportToEditId;

    if (!customReportId || alreadySelectedForViewOrEdit || alreadyOpeningDeleteModal) {
      return;
    }
    const readOnly = true;

    this.customReportsGridApi.forEachNode(node => {
      if (node.data.reportSetId === customReportId) {
        node.setSelected(true);
      }
    });

    showCustomReportFormForEdit(customReportId, readOnly);
  };

  showCustomReportsFormForAdd = () => this.props.showCustomReportFormForAdd(false);

  showCustomReportsFormForAddReportSet = () => this.props.showCustomReportFormForAdd(true);

  deselectCustomReport = () => {
    this.customReportsGridApi.deselectAll();
  };

  changeDisplayFlag = (shouldDisplay, { reportSetId }) => {
    this.props.updateDisplayFlag({
      reportSetId,
      reportSetDisplayFlag: !shouldDisplay,
    });
  };

  setCustomReportQueryModified = isCustomReportQueryModified => this.setState({ isCustomReportQueryModified });

  removeCustomReport = customReport => {
    this.props.deleteCustomReport(customReport);
    this.setCustomReportQueryModified(false);
    this.customReportsGridApi.forEachNode(((node, index) => {
      if (node.data.reportSetId === customReport.id) {
        this.props.updateRowToScrollTo(index - 1);
      }
    }));
  };

  componentToRenderOnSearchBar = () => (
    <div className="d-flex flex-row">
      <Button
        type="button"
        kind="primary"
        className="a-my-10"
        disabled={this.props.openFormForAdd}
        onClick={this.showCustomReportsFormForAdd}
      >
        Add Report
      </Button>

      <Button
        type="button"
        kind="secondary"
        className="a-m-10"
        disabled={this.props.openFormForAdd}
        onClick={this.showCustomReportsFormForAddReportSet}
      >
        Add Report Set
      </Button>
    </div>
  );

  render() {
    const {
      openFormForAdd,
      openFormForEdit,
      canModify,
      customReports,
      isFetchingCustomReports,
      isTestCustomReportQueryInProgress,
      deleteModalData,
      customReportToEditId,
      customRepostInsertQueryModalData,
    } = this.props;

    const {
      isCustomReportQueryModified,
    } = this.state;

    return (
      <div className="custom-reports-page">
        <div className="page-without-secondary-menu">
          <div className="container-fluid">
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Custom Reports Management</h5>
                </div>
              </div>
            </div>
            <div className="row no-gutters page-content-container">
              <div className="col">
                <CustomReportsGrid
                  componentToRenderOnSearchBar={this.componentToRenderOnSearchBar()}
                  changeDisplayFlag={this.changeDisplayFlag}
                  onCustomReportsGridReady={this.onCustomReportGridReady}
                  shouldDisplayAdminColumn={canModify}
                  showCustomReportsFormForView={this.showCustomReportQueryForm}
                  isCustomReportQueryModified={isCustomReportQueryModified}
                  customReports={customReports}
                  isGridLoading={isFetchingCustomReports}
                  customReportToEditId={customReportToEditId}
                />
              </div>
            </div>
            <div className="row no-gutters">
              <div className="col">
                {openFormForAdd ? (
                  <CustomReportsEditorForm
                    deselectCustomReport={this.deselectCustomReport}
                    setCustomReportQueryModified={this.setCustomReportQueryModified}
                    showCustomReportQueryForm={this.showCustomReportQueryForm}
                    isTestCustomReportQueryInProgress={isTestCustomReportQueryInProgress}
                    customReports={customReports}
                  />
                ) : null}
                {openFormForEdit ? (
                  <CustomReportsEditorForm
                    deselectCustomReport={this.deselectCustomReport}
                    setCustomReportQueryModified={this.setCustomReportQueryModified}
                    showCustomReportQueryForm={this.showCustomReportQueryForm}
                    isTestCustomReportQueryInProgress={isTestCustomReportQueryInProgress}
                    customReports={customReports}
                    openForEdit
                  />) : null}
              </div>
            </div>
            {isTestCustomReportQueryInProgress
            && (
              <div className="modal-loading">
                <Spinner/>
              </div>
            )
            }
          </div>

          <DeleteCustomReportModal
            entityToRemove={deleteModalData.data}
            removeEntity={this.removeCustomReport}
            isVisible={deleteModalData.show}
          />

          <ExportCustomRepostInsertQueryModal
            reportSetId={customRepostInsertQueryModalData.data?.reportSetId}
            isVisible={customRepostInsertQueryModalData.show}
          />

        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  customReports: customReportsSelector(state),
  openFormForAdd: openFormForAddSelector(state),
  openFormForEdit: openFormForEditSelector(state),
  customReportToEditId: customReportIdSelector(state),
  deleteModalData: modalDataSelector(state, ModalNames.CustomReportDelete),
  customRepostInsertQueryModalData: modalDataSelector(state, ModalNames.ExportCustomReportInsertQuery),
  isFetchingCustomReports: isFetchingCustomReportsSelector(state),
  isTestCustomReportQueryInProgress: isTestCustomReportQueryInProgressSelector(state),
  rowToScrollTo: rowToScrollToSelector(state),
  addedReportId: addedReportIdSelector(state),
});

export default withPermissions(permissions.CUSTOM_REPORTS_MANAGEMENT)(connect(
  mapStateToProps,
  {
    showCustomReportFormForAdd,
    showCustomReportFormForEdit,
    fetchCustomReports,
    updateDisplayFlag,
    updateRowToScrollTo,
    resetRowToScrollTo,
    deleteCustomReport,
  }
)(CustomReportsManagementPage));
